import { useCallback } from 'react';
import { useCustomerStore } from '../store/customer';
import { useSessionStore } from '../store/session';
import { useUIStore } from '../store/ui';
import customerService from '../services/customerService';
import { handleError } from '../lib/errors/handleError';

export const useCustomer = () => {
  const { customer, featureToggles, setCustomer } = useCustomerStore();
  const { msisdn } = useSessionStore();
  const { showError, showLoading, hideLoading } = useUIStore();

  const favoriteCard = useCallback(
    async (token: string) => {
      showLoading();
      try {
        await customerService.favoriteCard(msisdn, token);
        const { data } = await customerService.getCustomer(msisdn);
        setCustomer(data);
      } catch (err) {
        handleError(err, showError);
      } finally {
        hideLoading();
      }
    },
    [msisdn, setCustomer, showError, showLoading, hideLoading],
  );

  const deleteCard = useCallback(
    async (token: string) => {
      showLoading();
      try {
        await customerService.deleteCard(msisdn, token);
        const { data } = await customerService.getCustomer(msisdn);
        setCustomer(data);
      } catch (err) {
        handleError(err, showError);
      } finally {
        hideLoading();
      }
    },
    [msisdn, setCustomer, showError, showLoading, hideLoading],
  );

  const addCard = useCallback(
    async (cardKey: string) => {
      showLoading();
      try {
        await customerService.addCard(msisdn, cardKey);
        const { data } = await customerService.getCustomer(msisdn);
        setCustomer(data);
      } catch (err) {
        handleError(err, showError);
        throw err;
      } finally {
        hideLoading();
      }
    },
    [msisdn, setCustomer, showError, showLoading, hideLoading],
  );

  const deleteCustomer = useCallback(
    async (customerId: string) => {
      showLoading();
      try {
        await customerService.deleteCustomer(msisdn, customerId);
      } catch (err) {
        handleError(err, showError);
        throw err;
      } finally {
        hideLoading();
      }
    },
    [msisdn, showError, showLoading, hideLoading],
  );

  // Refresh silencioso do customer — sem loading overlay, falha não bloqueia.
  // Chamado em pontos de sucesso/processamento de recarga (crédito não-checkout,
  // checkout confirmed/pending) para refletir mudanças do BFF (cartões,
  // benefícios, traits) sem ação explícita do usuário.
  const refreshCustomer = useCallback(async () => {
    if (!msisdn) return;
    try {
      const { data } = await customerService.getCustomer(msisdn);
      setCustomer(data);
    } catch {
      // Falha silenciosa — refresh em background não atrapalha o fluxo do user
    }
  }, [msisdn, setCustomer]);

  const isTopupCampaignEligible =
    customer?.traits?.topup_campaign?.value === 'upsell_hybrid';
  const hasCards = (customer?.credit_cards?.length ?? 0) > 0;
  const creditCards = customer?.credit_cards || [];

  const getLatestFeatureToggles = () =>
    useCustomerStore.getState().featureToggles;

  return {
    customer,
    featureToggles,
    isTopupCampaignEligible,
    hasCards,
    creditCards,
    getLatestFeatureToggles,
    favoriteCard,
    deleteCard,
    addCard,
    deleteCustomer,
    refreshCustomer,
  };
};
